import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';

import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import {
  getSizeConfirmationApi, submitSizeConfirmationApi,
} from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';

/**
 * "Which size next time?" — asked where an employee will actually meet it.
 *
 * My Uniforms carries the same question, but only for someone who goes
 * looking. Most people do not, and the answer has a deadline: once the window
 * shuts, procurement orders whatever size is on file. So this mounts app-wide
 * beside PendingAcceptanceModal and asks on login.
 *
 * It asks only while the window is open and only until answered, and neither
 * of those is decided here — ?pending=1 returns exactly the uniforms still
 * waiting, so an answered or out-of-window uniform simply stops coming back.
 * Nothing in this file recomputes a deadline.
 *
 * Keeping the same size is a real answer, so it is the default and the button
 * says so. A uniform that comes in one size never reaches here at all.
 */

const toastOptions = { position: 'top-right', autoClose: 2500, transition: Bounce };

const SizeConfirmationModal = () => {
  const [visible, setVisible] = useState(false);
  const [pending, setPending] = useState([]);
  const [chosen, setChosen] = useState({});
  const [savingItem, setSavingItem] = useState(null);

  useEffect(() => {
    getSizeConfirmationApi({ pending: 1 })
      .then(response => {
        const rows = response.data?.data || [];
        setPending(rows);
        setChosen(Object.fromEntries(
          rows.map(row => [row.uniform_item, row.preferred_size ?? null]),
        ));
        if (rows.length > 0) setVisible(true);
      })
      // Silent: this is a prompt, not something the employee asked for, and
      // an error toast on every login would be worse than not asking.
      .catch(() => {});
  }, []);

  const answered = (itemId) => {
    setPending(prev => {
      const next = prev.filter(row => row.uniform_item !== itemId);
      if (next.length === 0) setVisible(false);
      return next;
    });
  };

  const save = (row) => {
    const size = chosen[row.uniform_item];
    if (!size) {
      toast.error('Choose a size first', { ...toastOptions, autoClose: 3000 });
      return;
    }
    setSavingItem(row.uniform_item);
    submitSizeConfirmationApi({
      selections: [{ uniform_item: row.uniform_item, size }],
    })
      .then(() => {
        toast.success(`Size confirmed for ${row.uniform_item_name}`, toastOptions);
        answered(row.uniform_item);
      })
      .catch(e => {
        // The uniform stays on screen: the answer was not recorded, and
        // removing it would leave the employee believing it was.
        const message = e.response?.data?.error || e.response?.data?.detail
          || 'Could not save your size. Please try again.';
        toast.error(message, { ...toastOptions, autoClose: 4000 });
      })
      .finally(() => setSavingItem(null));
  };

  if (!visible || pending.length === 0) return null;

  return (
    <Modal
      title={(
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            Confirm your uniform size
          </div>
          <div style={{ fontSize: 13, fontWeight: 400, color: '#6B7280', marginTop: 4 }}>
            {pending.length === 1
              ? 'Your next uniform is ordered in the size you save here'
              : `${pending.length} uniforms are due for replacement soon`}
          </div>
        </div>
      )}
      open={visible}
      onCancel={() => setVisible(false)}
      maskClosable={false}
      keyboard={false}
      width={640}
      centered
      zIndex={1200}
      bodyStyle={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 8px 0' }}
      footer={[
        <button
          key="skip" type="button" className="btn btn-light"
          onClick={() => setVisible(false)} disabled={savingItem !== null}
        >
          Skip for now
        </button>,
      ]}
    >
      {pending.map(row => {
        const options = (row.available_sizes || []).map(size => ({
          label: `${size.name} (${size.code})`, value: size.id,
        }));
        const value = options.find(o => o.value === chosen[row.uniform_item]) || null;
        const unchanged = chosen[row.uniform_item] === row.preferred_size;
        const days = row.days_left_to_change;

        return (
          <div
            key={row.uniform_item} className="p-3 mb-2"
            style={{ background: '#F9FAFB', borderRadius: 12, border: '1px solid #F1F2F4' }}
          >
            <div className="d-flex align-items-start gap-3">
              {row.uniform_item_image ? (
                <img
                  src={row.uniform_item_image} alt=""
                  style={{
                    width: 56, height: 56, objectFit: 'contain', borderRadius: 8,
                    background: '#fff', flexShrink: 0,
                  }}
                />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: 8, background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, color: '#9CA3AF', border: '1px solid #F1F2F4',
                }}
                >
                  <i className="fa fa-tshirt" />
                </div>
              )}

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                  {row.uniform_item_name}
                </div>
                <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  Your size {row.preferred_size_code || '—'}
                  {typeof days === 'number' && (
                    <>
                      {' · '}
                      {days === 0 ? 'last day' : `${days} day${days === 1 ? '' : 's'} left`}
                    </>
                  )}
                  {row.change_deadline && (
                    <>{' · '}closes {formatDateHandler(row.change_deadline)}</>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-3 align-items-start">
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <label className="visually-hidden" htmlFor={`size-${row.uniform_item}`}>
                  {`Size for ${row.uniform_item_name}`}
                </label>
                <FilterSelect
                  inputId={`size-${row.uniform_item}`}
                  onChange={(opt) => setChosen(prev => ({
                    ...prev, [row.uniform_item]: opt?.value ?? null,
                  }))}
                  optionLabel="label" optionValue="value"
                  options={options} value={value}
                  placeholder="Select size"
                  noOptionsLabel="sizes"
                  searchable={false}
                />
              </div>
              <Button
                label={
                  unchanged
                    ? `Keep size ${row.preferred_size_code || ''}`.trim()
                    : 'Save size'
                }
                className="btn btn-primary btn-sm"
                loading={savingItem === row.uniform_item}
                disabled={savingItem !== null}
                onClick={() => save(row)}
              />
            </div>
          </div>
        );
      })}
    </Modal>
  );
};

export default SizeConfirmationModal;
